export interface WizardOption {
  text: string;
  value: string | number | boolean;
  nextStepId: string; // ID of the next question/step
}

export interface WizardQuestion {
  id: string; // Unique ID for this question/step (e.g., 'step1_age')
  stepNumber: number; // Which major step it belongs to (for progress indicator)
  stepLabel: string; // Label for the step (e.g., 'About You')
  text: string; // The question text
  options: WizardOption[];
  // Optional: For Step 5 questions, indicate which priority triggers them
  dependsOnPriority?: string; 
  // Optional: Define if the question allows multiple selections
  isMultiSelect?: boolean; 
  maxSelections?: number; // If multi-select, max number allowed
  // Removed isContactStep?: boolean; 
}

// Define the structure to hold all questions, keyed by their ID
export const wizardQuestions: Record<string, WizardQuestion> = {
  // --- Step 1: About You --- (Back to Step 1)
  step1_age: {
    id: 'step1_age',
    stepNumber: 1, // Back to 1
    stepLabel: 'About You',
    text: 'What is your current age range?',
    options: [
      { text: 'Under 25', value: 'under_25', nextStepId: 'step1_status' },
      { text: '25-34', value: '25_34', nextStepId: 'step1_status' },
      { text: '35-44', value: '35_44', nextStepId: 'step1_status' },
      { text: '45-54', value: '45_54', nextStepId: 'step1_status' },
      { text: '55-64', value: '55_64', nextStepId: 'step1_status' },
      { text: '65+', value: '65_plus', nextStepId: 'step1_status' },
    ],
  },
  step1_status: {
    id: 'step1_status',
    stepNumber: 1, // Back to 1
    stepLabel: 'About You',
    text: 'What is your relationship status?',
    options: [
      { text: 'Single', value: 'single', nextStepId: 'step1_dependents' },
      { text: 'In a relationship / Married', value: 'married_partner', nextStepId: 'step1_dependents' },
      { text: 'Divorced / Separated', value: 'divorced_separated', nextStepId: 'step1_dependents' },
      { text: 'Widowed', value: 'widowed', nextStepId: 'step1_dependents' },
    ],
  },
  step1_dependents: {
    id: 'step1_dependents',
    stepNumber: 1, // Back to 1
    stepLabel: 'About You',
    text: 'Do you have any dependents?',
    options: [
      { text: 'No dependents', value: 'none', nextStepId: 'step2_employment' },
      { text: 'Children under 18', value: 'children_under_18', nextStepId: 'step2_employment' },
      { text: 'Adult children', value: 'children_adult', nextStepId: 'step2_employment' },
      { text: 'Other dependents', value: 'other_dependents', nextStepId: 'step2_employment' },
    ],
  },

  // --- Step 2: Your Work & Finances --- (Back to Step 2)
  step2_employment: {
    id: 'step2_employment',
    stepNumber: 2, // Back to 2
    stepLabel: 'Work & Finances',
    text: 'What is your current employment status?',
    options: [
      { text: 'Employed (Salaried)', value: 'employed_salary', nextStepId: 'step2_income' },
      { text: 'Self-Employed', value: 'self_employed', nextStepId: 'step2_income' },
      { text: 'Business Owner', value: 'business_owner', nextStepId: 'step2_income' },
      { text: 'Retired', value: 'retired', nextStepId: 'step2_income' },
      { text: 'Student', value: 'student', nextStepId: 'step2_income' },
      { text: 'Unemployed / Seeking', value: 'unemployed', nextStepId: 'step2_income' },
      { text: 'Other', value: 'other', nextStepId: 'step2_income' },
    ],
  },
  step2_income: {
    id: 'step2_income',
    stepNumber: 2, // Back to 2
    stepLabel: 'Work & Finances',
    text: 'What is your approximate annual household income?',
    options: [
      { text: '< $50,000', value: 'under_50k', nextStepId: 'step3_housing' },
      { text: '$50,000 - $99,999', value: '50k_99k', nextStepId: 'step3_housing' },
      { text: '$100,000 - $149,999', value: '100k_149k', nextStepId: 'step3_housing' },
      { text: '$150,000 - $249,999', value: '150k_249k', nextStepId: 'step3_housing' },
      { text: '$250,000+', value: '250k_plus', nextStepId: 'step3_housing' },
      { text: 'Prefer not to say', value: 'prefer_not_say', nextStepId: 'step3_housing' },
    ],
  },
  step2_employees: {
    id: 'step2_employees',
    stepNumber: 2, // Back to 2
    stepLabel: 'Work & Finances',
    text: 'How many employees does your business have?',
    options: [
      { text: 'Just me', value: '1', nextStepId: 'step3_housing' }, // Points to start of Step 3
      { text: '2-10', value: '2_10', nextStepId: 'step3_housing' },
      { text: '11-50', value: '11_50', nextStepId: 'step3_housing' },
      { text: '50+', value: '50_plus', nextStepId: 'step3_housing' },
    ],
  },

  // --- Step 3: Assets, Goals & Priorities --- (Back to Step 3)
  step3_housing: {
    id: 'step3_housing',
    stepNumber: 3, // Back to 3
    stepLabel: 'Assets, Goals & Priorities',
    text: 'What is your current housing situation?',
    options: [
      { text: 'Renting', value: 'renting', nextStepId: 'step3_assets' },
      { text: 'Own (with mortgage)', value: 'own_mortgage', nextStepId: 'step3_assets' },
      { text: 'Own (mortgage-free)', value: 'own_free', nextStepId: 'step3_assets' },
      { text: 'Living with family / Other', value: 'other', nextStepId: 'step3_assets' },
    ]
  },
  step3_assets: {
    id: 'step3_assets',
    stepNumber: 3, // Back to 3
    stepLabel: 'Assets, Goals & Priorities',
    text: 'Outside of your primary home, do you have other significant assets or investments (e.g., RRSPs, TFSAs, non-registered investments, rental properties)?',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step3_goals' },
      { text: 'No', value: false, nextStepId: 'step3_goals' },
      { text: 'Unsure / Prefer not to say', value: 'prefer_not_say', nextStepId: 'step3_goals' },
    ]
  },
  step3_goals: {
    id: 'step3_goals',
    stepNumber: 3, // Back to 3
    stepLabel: 'Assets, Goals & Priorities',
    text: 'What are your primary long-term financial goals? (Select all that apply)',
    isMultiSelect: true,
    options: [
      { text: 'Comfortable Retirement', value: 'retirement', nextStepId: 'step4_priorities' },
      { text: 'Buying Property', value: 'property', nextStepId: 'step4_priorities' },
      { text: 'Paying Down Debt', value: 'debt_paydown', nextStepId: 'step4_priorities' },
      { text: 'Saving for Education', value: 'education', nextStepId: 'step4_priorities' },
      { text: 'Starting/Growing a Business', value: 'business_start_grow', nextStepId: 'step4_priorities' },
      { text: 'Estate Planning / Legacy', value: 'legacy', nextStepId: 'step4_priorities' },
      { text: 'Major Purchase (vehicle, travel)', value: 'major_purchase', nextStepId: 'step4_priorities' },
      { text: 'Building Emergency Savings', value: 'emergency_fund', nextStepId: 'step4_priorities' },
      { text: 'Other / Not Sure', value: 'other', nextStepId: 'step4_priorities' },
    ]
  },
  step4_priorities: { // Still part of Step 3
    id: 'step4_priorities',
    stepNumber: 3, // Back to 3
    stepLabel: 'Assets, Goals & Priorities',
    text: 'What are your most important financial priorities right now? (Select all that apply)', // Updated text
    isMultiSelect: true,
    // maxSelections: 3, // Removed limit
    // nextStepId points to the start of Step 4 (Specific Needs)
    options: [
      { text: 'Protecting my income and family', value: 'protection', nextStepId: 'step5_needs_start' }, // Points to Step 4 start
      { text: 'Saving and investing for the future', value: 'savings', nextStepId: 'step5_needs_start' },
      { text: 'Planning for my business', value: 'business', nextStepId: 'step5_needs_start' },
      { text: 'Managing my retirement income', value: 'retirement_income', nextStepId: 'step5_needs_start' },
      { text: 'Planning my estate and legacy', value: 'estate', nextStepId: 'step5_needs_start' },
      { text: 'Advanced wealth strategies', value: 'advanced', nextStepId: 'step5_needs_start' },
      { text: 'Managing debt', value: 'debt_manage', nextStepId: 'step5_needs_start' },
      { text: 'Short-term savings goals', value: 'short_term', nextStepId: 'step5_needs_start' },
    ]
  },

  // --- Step 4: Specific Needs --- (Back to Step 4)
  // Protection Priority Questions
  step5_protection_existing_life: { 
    id: 'step5_protection_existing_life',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Do you currently have existing life insurance coverage?',
    dependsOnPriority: 'protection',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_protection_existing_ci_di' },
      { text: 'No', value: false, nextStepId: 'step5_protection_existing_ci_di' },
      { text: 'Unsure', value: 'unsure', nextStepId: 'step5_protection_existing_ci_di' },
    ]
  },
  step5_protection_existing_ci_di: {
    id: 'step5_protection_existing_ci_di',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Do you have disability or critical illness insurance?',
    dependsOnPriority: 'protection',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_protection_debt_concern' },
      { text: 'No', value: false, nextStepId: 'step5_protection_debt_concern' },
      { text: 'Unsure', value: 'unsure', nextStepId: 'step5_protection_debt_concern' },
    ]
  },
  step5_protection_debt_concern: {
    id: 'step5_protection_debt_concern',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Are you concerned about covering specific debts (like a mortgage) if unexpected events occur?',
    dependsOnPriority: 'protection',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_protection_primary_income' },
      { text: 'No', value: false, nextStepId: 'step5_protection_primary_income' },
    ]
  },
  step5_protection_primary_income: {
    id: 'step5_protection_primary_income',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Are you the primary income source for your household?',
    dependsOnPriority: 'protection',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_needs_start' }, // Points to Step 4 start
      { text: 'No', value: false, nextStepId: 'step5_needs_start' },
    ]
  },

  // Savings Priority Questions
  step5_savings_retire_age: {
    id: 'step5_savings_retire_age',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'What is your target retirement age or timeframe?',
    dependsOnPriority: 'savings',
    options: [
      { text: '< 5 years', value: 'lt_5', nextStepId: 'step5_savings_risk_tolerance' },
      { text: '5-15 years', value: '5_15', nextStepId: 'step5_savings_risk_tolerance' },
      { text: '15+ years', value: 'gt_15', nextStepId: 'step5_savings_risk_tolerance' },
      { text: 'Undecided', value: 'undecided', nextStepId: 'step5_savings_risk_tolerance' },
    ]
  },
  step5_savings_risk_tolerance: {
    id: 'step5_savings_risk_tolerance',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'How comfortable are you with investment risk?',
    dependsOnPriority: 'savings',
    options: [
      { text: 'Very Low', value: 'very_low', nextStepId: 'step5_savings_tfsa_rrsp' },
      { text: 'Low', value: 'low', nextStepId: 'step5_savings_tfsa_rrsp' },
      { text: 'Medium', value: 'medium', nextStepId: 'step5_savings_tfsa_rrsp' },
      { text: 'High', value: 'high', nextStepId: 'step5_savings_tfsa_rrsp' },
      { text: 'Very High', value: 'very_high', nextStepId: 'step5_savings_tfsa_rrsp' },
    ]
  },
  step5_savings_tfsa_rrsp: {
    id: 'step5_savings_tfsa_rrsp',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Are you currently utilizing RRSPs or TFSAs?',
    dependsOnPriority: 'savings',
    options: [
      { text: 'Yes, Both', value: 'both', nextStepId: 'step5_needs_start' }, // Points to Step 4 start
      { text: 'RRSP Only', value: 'rrsp', nextStepId: 'step5_needs_start' },
      { text: 'TFSA Only', value: 'tfsa', nextStepId: 'step5_needs_start' },
      { text: 'Neither', value: 'neither', nextStepId: 'step5_needs_start' },
      { text: 'Unsure', value: 'unsure', nextStepId: 'step5_needs_start' },
    ]
  },

  // Business Priority Questions
  step5_business_objective: {
    id: 'step5_business_objective',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'What is your main business planning objective?',
    dependsOnPriority: 'business',
    options: [
      { text: 'Owner Retirement', value: 'owner_retirement', nextStepId: 'step5_business_key_person' },
      { text: 'Succession Planning', value: 'succession', nextStepId: 'step5_business_key_person' },
      { text: 'Employee Retention/Benefits', value: 'benefits', nextStepId: 'step5_business_key_person' },
      { text: 'Tax Strategy', value: 'tax', nextStepId: 'step5_business_key_person' },
      { text: 'Risk Management', value: 'risk', nextStepId: 'step5_business_key_person' },
    ]
  },
  step5_business_key_person: {
    id: 'step5_business_key_person',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Does your business rely heavily on specific key individuals (including yourself or partners)?',
    dependsOnPriority: 'business',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_needs_start' }, // Points to Step 4 start
      { text: 'No', value: false, nextStepId: 'step5_needs_start' },
    ]
  },

  // Retirement Income Priority Questions
  step5_retirement_income_timing: {
    id: 'step5_retirement_income_timing',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Are you currently retired or planning to retire within the next 5 years?',
    dependsOnPriority: 'retirement_income',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_retirement_income_goal' },
      { text: 'No', value: false, nextStepId: 'step5_retirement_income_goal' },
    ]
  },
  step5_retirement_income_goal: {
    id: 'step5_retirement_income_goal',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'What is your primary goal for retirement income?',
    dependsOnPriority: 'retirement_income',
    options: [
      { text: 'Guaranteed income for life', value: 'guaranteed_income', nextStepId: 'step5_retirement_income_sources' },
      { text: 'Flexible access to funds', value: 'flexible_access', nextStepId: 'step5_retirement_income_sources' },
      { text: 'Maximizing potential growth', value: 'max_growth', nextStepId: 'step5_retirement_income_sources' },
      { text: 'Preserving capital', value: 'preserve_capital', nextStepId: 'step5_retirement_income_sources' },
    ]
  },
  step5_retirement_income_sources: {
    id: 'step5_retirement_income_sources',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Do you have existing sources of retirement income (e.g., pensions, RRIFs, annuities)?',
    dependsOnPriority: 'retirement_income',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_needs_start' }, // Points to Step 4 start
      { text: 'No', value: false, nextStepId: 'step5_needs_start' },
      { text: 'Unsure', value: 'unsure', nextStepId: 'step5_needs_start' },
    ]
  },

  // Estate & Legacy Priority Questions
  step5_estate_will_poa: {
    id: 'step5_estate_will_poa',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Do you have a current Will and Power of Attorney?',
    dependsOnPriority: 'estate',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_estate_legacy_interest' },
      { text: 'No', value: false, nextStepId: 'step5_estate_legacy_interest' },
      { text: 'Unsure', value: 'unsure', nextStepId: 'step5_estate_legacy_interest' },
    ]
  },
  step5_estate_legacy_interest: {
    id: 'step5_estate_legacy_interest',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Are you interested in leaving a legacy to specific individuals or charities?',
    dependsOnPriority: 'estate',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_estate_tax_concern' },
      { text: 'No', value: false, nextStepId: 'step5_estate_tax_concern' },
    ]
  },
  step5_estate_tax_concern: {
    id: 'step5_estate_tax_concern',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Are you concerned about potential estate taxes or probate fees?',
    dependsOnPriority: 'estate',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_estate_value' },
      { text: 'No', value: false, nextStepId: 'step5_estate_value' },
      { text: 'Unsure', value: 'unsure', nextStepId: 'step5_estate_value' },
    ]
  },
  step5_estate_value: {
    id: 'step5_estate_value',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'What is the approximate value of the estate you anticipate leaving?',
    dependsOnPriority: 'estate',
    options: [
      { text: '< $500,000', value: 'lt_500k', nextStepId: 'step5_needs_start' }, // Points to Step 4 start
      { text: '$500,000 - $1 Million', value: '500k_1m', nextStepId: 'step5_needs_start' },
      { text: '$1 Million - $5 Million', value: '1m_5m', nextStepId: 'step5_needs_start' },
      { text: '$5 Million+', value: 'gt_5m', nextStepId: 'step5_needs_start' },
      { text: 'Prefer not to say', value: 'prefer_not_say', nextStepId: 'step5_needs_start' },
    ]
  },

  // Advanced Wealth Priority Questions
  step5_advanced_corp_assets: {
    id: 'step5_advanced_corp_assets',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Do you hold significant assets within a corporation?',
    dependsOnPriority: 'advanced',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_advanced_min_tax' },
      { text: 'No', value: false, nextStepId: 'step5_advanced_min_tax' },
    ]
  },
  step5_advanced_min_tax: {
    id: 'step5_advanced_min_tax',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Are you looking for strategies to minimize taxes on investment growth or during wealth transfer?',
    dependsOnPriority: 'advanced',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_advanced_leveraged' },
      { text: 'No', value: false, nextStepId: 'step5_advanced_leveraged' },
    ]
  },
  step5_advanced_leveraged: {
    id: 'step5_advanced_leveraged',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Are you interested in leveraged investment strategies (e.g., using borrowed funds)?',
    dependsOnPriority: 'advanced',
    options: [
      { text: 'Yes', value: true, nextStepId: 'step5_needs_start' }, // Points to Step 4 start
      { text: 'No', value: false, nextStepId: 'step5_needs_start' },
      { text: 'Tell me more', value: 'tell_more', nextStepId: 'step5_needs_start' },
    ]
  },

  // Managing Debt Priority Questions
  step5_debt_type: {
    id: 'step5_debt_type',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'What type of debt are you primarily focused on managing? (Select all that apply)',
    dependsOnPriority: 'debt_manage',
    isMultiSelect: true,
    options: [
      { text: 'Mortgage', value: 'mortgage', nextStepId: 'step5_debt_goal' },
      { text: 'Credit Cards', value: 'credit_card', nextStepId: 'step5_debt_goal' },
      { text: 'Lines of Credit', value: 'loc', nextStepId: 'step5_debt_goal' },
      { text: 'Student Loans', value: 'student_loan', nextStepId: 'step5_debt_goal' },
      { text: 'Business Debt', value: 'business_debt', nextStepId: 'step5_debt_goal' },
      { text: 'Other', value: 'other', nextStepId: 'step5_debt_goal' },
    ]
  },
  step5_debt_goal: {
    id: 'step5_debt_goal',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'Is your goal to consolidate debt, reduce interest costs, or pay it off faster? (Select all that apply)',
    dependsOnPriority: 'debt_manage',
    isMultiSelect: true,
    options: [
      { text: 'Consolidate Debt', value: 'consolidate', nextStepId: 'step5_needs_start' }, // Points to Step 4 start
      { text: 'Reduce Interest Costs', value: 'reduce_interest', nextStepId: 'step5_needs_start' },
      { text: 'Pay Off Faster', value: 'pay_faster', nextStepId: 'step5_needs_start' },
    ]
  },

  // Short-term Savings Priority Questions
  step5_short_term_purpose: {
    id: 'step5_short_term_purpose',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'What is the primary purpose of your short-term savings?',
    dependsOnPriority: 'short_term',
    options: [
      { text: 'Emergency Fund', value: 'emergency', nextStepId: 'step5_short_term_timeframe' },
      { text: 'Down Payment', value: 'down_payment', nextStepId: 'step5_short_term_timeframe' },
      { text: 'Major Purchase', value: 'purchase', nextStepId: 'step5_short_term_timeframe' },
      { text: 'Travel', value: 'travel', nextStepId: 'step5_short_term_timeframe' },
      { text: 'Other', value: 'other', nextStepId: 'step5_short_term_timeframe' },
    ]
  },
  step5_short_term_timeframe: {
    id: 'step5_short_term_timeframe',
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: 'What is your timeframe for needing these funds?',
    dependsOnPriority: 'short_term',
    options: [
      { text: '< 1 year', value: 'lt_1y', nextStepId: 'step5_needs_start' }, // Points to Step 4 start
      { text: '1-3 years', value: '1_3y', nextStepId: 'step5_needs_start' },
      { text: '3-5 years', value: '3_5y', nextStepId: 'step5_needs_start' },
    ]
  },

  // --- Step 4 Needs Start (Used for jump logic) --- // Back to Step 4
  step5_needs_start: { 
    id: 'step5_needs_start', // Keep ID consistent with options above
    stepNumber: 4, // Back to 4
    stepLabel: 'Specific Needs',
    text: '', 
    options: [], 
  },

  // --- Step 5: Results --- // Back to Step 5
  results: {
    id: 'results',
    stepNumber: 5, // Back to 5
    stepLabel: 'Results',
    text: 'Results Placeholder',
    options: []
  }
};

// Helper function to get the total number of steps (back to 5)
export const getTotalSteps = (): number => {
  // Steps are: 1. About You, 2. Work & Finances, 3. Assets/Goals/Priorities, 4. Specific Needs, 5. Results
  return 5;
};
